import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  LoadingState,
  PageHeader,
  actionsColumn,
  dateColumn,
  statusColumn,
  textColumn,
  type ColumnDef,
} from '@/components/ui'
import { customerApi } from '../lib/api'
import { useCustomerAuthStore } from '../stores/auth'

interface TicketRow {
  id: number
  nr: string | null
  subject: string
  status: string
  priority: string
  createdAt: string | null
  dueDate: string | null
}

interface OposResponse {
  data: unknown[]
  total: number
}

interface ContractRow {
  id: string
  name: string
  status: string
  monthlyValue: number
}

type Metric =
  | { state: 'loading' }
  | { state: 'ready'; value: string; hint?: string }
  | { state: 'error' }

function fmtMoney(n: number) {
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
}

const CLOSED_STATUSES = new Set([
  'closed', 'geschlossen', 'erledigt', 'done', 'abgeschlossen', 'resolved', 'gelöst',
])

function isOpen(status: string) {
  return !CLOSED_STATUSES.has(status.trim().toLowerCase())
}

function firstName(full: string | undefined) {
  if (!full) return ''
  const part = full.trim().split(/\s+/)[0]
  return part ?? ''
}

function MetricCard({
  label,
  metric,
  to,
  navigate,
}: {
  label: string
  metric: Metric
  to: string
  navigate: (to: string) => void
}) {
  return (
    <Card
      onClick={() => navigate(to)}
      className="cursor-pointer transition hover:border-brand-300 hover:shadow-card focus-within:ring-2 focus-within:ring-brand-400"
    >
      <Card.Body>
        <div className="text-small text-gray-500">{label}</div>
        <div className="mt-2 text-h1 text-gray-900">
          {metric.state === 'loading' && <span className="text-gray-300">…</span>}
          {metric.state === 'error' && <span className="text-gray-400">—</span>}
          {metric.state === 'ready' && metric.value}
        </div>
        {metric.state === 'ready' && metric.hint && (
          <div className="mt-1 text-caption text-gray-500">{metric.hint}</div>
        )}
        {metric.state === 'error' && (
          <div className="mt-1 text-caption text-gray-400">Aktuell nicht verfügbar</div>
        )}
      </Card.Body>
    </Card>
  )
}

export function CustomerHomePage() {
  const user = useCustomerAuthStore((s) => s.user)
  const navigate = useNavigate()

  const [openTickets, setOpenTickets] = useState<Metric>({ state: 'loading' })
  const [opos, setOpos] = useState<Metric>({ state: 'loading' })
  const [contracts, setContracts] = useState<Metric>({ state: 'loading' })
  const [recentTickets, setRecentTickets] = useState<TicketRow[]>([])
  const [ticketsLoaded, setTicketsLoaded] = useState(false)

  useEffect(() => {
    customerApi.get<{ data: TicketRow[] }>('/tickets')
      .then((res) => {
        const all = res.data?.data ?? []
        const open = all.filter((t) => isOpen(t.status))
        const latest = [...all]
          .sort((a, b) => {
            const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
            const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
            return tb - ta
          })
          .slice(0, 5)
        setRecentTickets(latest)
        setOpenTickets({
          state: 'ready',
          value: String(open.length),
          hint: all.length === 0 ? 'Keine Tickets vorhanden' : `${all.length} insgesamt`,
        })
      })
      .catch(() => setOpenTickets({ state: 'error' }))
      .finally(() => setTicketsLoaded(true))

    customerApi.get<OposResponse>('/sap/opos')
      .then((res) => {
        const total = res.data?.total ?? 0
        const count = res.data?.data?.length ?? 0
        setOpos({
          state: 'ready',
          value: fmtMoney(total),
          hint: count === 0 ? 'Keine offenen Posten' : `${count} ${count === 1 ? 'Beleg' : 'Belege'}`,
        })
      })
      .catch(() => setOpos({ state: 'error' }))

    customerApi.get<{ data: ContractRow[] }>('/contracts')
      .then((res) => {
        const rows = res.data?.data ?? []
        setContracts({
          state: 'ready',
          value: String(rows.length),
          hint: rows.length === 0 ? 'Keine Verträge hinterlegt' : undefined,
        })
      })
      .catch(() => setContracts({ state: 'error' }))
  }, [])

  const greeting = firstName(user?.name) ? `Hallo ${firstName(user?.name)},` : 'Hallo,'

  const recentColumns = useMemo<ColumnDef<TicketRow, unknown>[]>(
    () => [
      {
        accessorKey: 'nr',
        header: 'Nr.',
        cell: ({ row }) => row.original.nr ?? row.original.id,
      },
      textColumn<TicketRow>('subject', 'Betreff'),
      statusColumn<TicketRow>('status'),
      dateColumn<TicketRow>('createdAt', 'Erstellt'),
      actionsColumn<TicketRow>('', (row) => (
        <Button variant="link" size="sm" onClick={() => navigate(`/tickets/${row.id}`)}>
          Öffnen
        </Button>
      )),
    ],
    [navigate],
  )

  return (
    <div>
      <PageHeader
        title={greeting}
        description="Willkommen im implec Kundenportal. Hier findest du alles auf einen Blick – Tickets, Dokumente und Verträge."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard label="Offene Tickets" metric={openTickets} to="/tickets" navigate={navigate} />
        <MetricCard label="Offene Posten" metric={opos} to="/dokumente/opos" navigate={navigate} />
        <MetricCard label="Verträge" metric={contracts} to="/vertraege" navigate={navigate} />
      </div>

      <div className="mt-6">
        <Card>
          <Card.Header>
            <Card.Title>Schnellaktionen</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => navigate('/tickets/neu')}>
                Neues Ticket
              </Button>
              <Button variant="secondary" onClick={() => navigate('/tickets/neu?followUp=callback')}>
                Rückruf anfordern
              </Button>
              <Button variant="secondary" onClick={() => navigate('/tickets/neu?followUp=appointment')}>
                Termin vereinbaren
              </Button>
              <Button variant="ghost" onClick={() => navigate('/dokumente/rechnungen')}>
                Rechnungen ansehen
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="mt-6">
        <Card>
          <Card.Header>
            <Card.Title>Letzte Tickets</Card.Title>
          </Card.Header>
          <Card.Body>
            {!ticketsLoaded ? (
              <LoadingState size="sm" />
            ) : recentTickets.length === 0 ? (
              <EmptyState
                size="sm"
                title="Keine Tickets"
                description="Du hast aktuell keine Tickets."
                action={
                  <Button variant="primary" size="sm" onClick={() => navigate('/tickets/neu')}>
                    Neues Ticket anlegen
                  </Button>
                }
              />
            ) : (
              <>
                <DataTable<TicketRow>
                  data={recentTickets}
                  columns={recentColumns}
                  getRowId={(t) => String(t.id)}
                />
                <div className="mt-3 text-right">
                  <Button variant="link" size="sm" onClick={() => navigate('/tickets')}>
                    Alle Tickets →
                  </Button>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}
